/**
 * The nine authenticated meta routes and the nav metadata that describes them.
 *
 * This is data, not behaviour, so `main.ts`'s router and the nav (bottom tabs on mobile, side
 * rail on desktop) read from exactly one list rather than two that can drift.
 */

import { t } from '../i18n';
import type { IconName } from './ui';

export interface RouteMeta {
  path: string;
  /**
   * The nav label, translated.
   *
   * A GETTER, not a plain string, and that is the load-bearing part: this list is a
   * module-level constant, so a literal `t('nav.play')` here would be resolved once when the
   * module is first imported and then hold that language for the life of the tab — a remount
   * on `setLocale` re-reads `route.label`, it does not re-evaluate this file. Reading through
   * a getter makes every read a fresh lookup, which costs nothing and cannot go stale.
   */
  readonly label: string;
  /**
   * The drawn icon for this route, from `ui.ts`'s kit.
   *
   * Declared here rather than looked up per nav surface, because there are two of them (the
   * desktop rail and the mobile tab bar) and a second lookup table is a second place to
   * forget a route.
   */
  icon: IconName;
}

export const META_ROUTES: RouteMeta[] = [
  { path: '/play', get label() { return t('nav.play'); }, icon: 'play' },
  { path: '/loadout', get label() { return t('nav.loadout'); }, icon: 'loadout' },
  { path: '/shop', get label() { return t('nav.shop'); }, icon: 'shop' },
  { path: '/career', get label() { return t('nav.career'); }, icon: 'career' },
  { path: '/leaderboards', get label() { return t('nav.leaderboards'); }, icon: 'leaderboards' },
  { path: '/quests', get label() { return t('nav.quests'); }, icon: 'quests' },
  { path: '/settings', get label() { return t('nav.settings'); }, icon: 'settings' },
];

const META_ROUTE_PATHS = new Set(META_ROUTES.map((r) => r.path));

/**
 * Paths that no longer exist, and where they went.
 *
 * `/armoury` was folded into `/loadout` on 2026-09-16 (`design/gdd/meta-screens.md`). A link
 * outlives the screen it pointed at — a bookmark, a message, the browser's own history — so a
 * retired path resolves to its successor rather than silently landing on Play, which would
 * look like the click did nothing.
 */
const RETIRED_ROUTES: Record<string, string> = {
  '/armoury': '/loadout',
  // The Locker's equipping went to Loadout and its sell-back became the marketplace's Sell
  // tab, so "everything I own" is answered in two places now and neither of them is a screen
  // of its own.
  '/inventory': '/shop',
};

export const DEFAULT_META_ROUTE = '/play';

export function isMetaRoute(path: string): boolean {
  return META_ROUTE_PATHS.has(path);
}

/**
 * `/` and any unrecognised authenticated path resolve to `/play` — the router's fallback for
 * this whole group, matching the "unknown authed path -> /play" rule.
 */
export function resolveMetaPath(path: string): string {
  if (path === '/') return DEFAULT_META_ROUTE;
  const successor = RETIRED_ROUTES[path];
  if (successor) return successor;
  return isMetaRoute(path) ? path : DEFAULT_META_ROUTE;
}
